<template>
  <div class="app-wrap" data-qa="patient-payment-receipt-page">
    <div v-if="loading" class="loading-wrapper">
      <updox-loading color="#003462" size="80px" />
    </div>
    <div v-else-if="error">
      <updox-error :error-details="errorDetails" />
    </div>
    <div v-else-if="receiptInfoStatus === 'PAYMENT_INVALID_STATE'">
      <section class="main-header">
        <div class="primary-header page-wrapper flex-column align-center">
          <h2>{{ practiceName }}</h2>
          <small>{{ practicePhone }}</small>
        </div>
      </section>
      <section class="page-wrapper">
        <updox-payment-invalid-state :invalid-state="receiptInvalidState" />
      </section>
    </div>
    <div v-else-if="receiptInfoStatus === 'TOKEN_NOT_FOUND'">
      <updox-bad-payment-request-token />
    </div>
    <div v-else-if="receiptInfoStatus === 'PRACTICE_UNABLE_TO_PROCESS'">
      <updox-cannot-process-payments />
    </div>
    <div v-else>
      <section class="main-header">
        <div class="primary-header page-wrapper flex-column align-center">
          <h2>{{ practiceName }}</h2>
          <small>{{ practicePhone }}</small>
        </div>
      </section>
      <section class="page-wrapper">
        <h3>Payment successful</h3>
        <div class="form-container">
          <div class="form-body">
            <div class="payment-preview">
              <div class="requested-details flex justify-between">
                <div class="flex-column">
                  <h2>
                    {{ datePaid }}
                  </h2>
                  <small>Date paid</small>
                </div>
                <div class="flex-column">
                  <h2>
                    {{ amount }}
                  </h2>
                  <small>Amount paid</small>
                </div>
              </div>
            </div>
            <div class="form-field">
              <h3>Auth code: {{ authorizationCode }}</h3>
              <p>Thank you for your payment!</p>
            </div>
          </div>
          <section class="form-actions">
            <updox-button
              text="Download Receipt"
              aria-label="Download Receipt"
              id="submitDownloadReceiptButton"
              @click.prevent="onDownload"
              :loading="currentlySubmitting"
            />
          </section>
        </div>
      </section>
    </div>
    <updox-footer />
  </div>
</template>

<script>
  import UpdoxFooter from "../components/common/UpdoxFooter";
  import SimplePaymentsPatientService from "@/services/SimplePaymentsPatientService";
  import UpdoxBadPaymentRequestToken from "../components/common/UpdoxBadPaymentRequestToken";
  import UpdoxPaymentInvalidState from "../components/common/UpdoxPaymentInvalidState";
  import UpdoxCannotProcessPayments from "../components/common/UpdoxCannotProcessPayments";
  import UpdoxError from "../components/common/UpdoxError";
  import { UpdoxButton }  from "@updox/common-vue";
  import { UpdoxLoading } from "@updox/common-vue";
  import {
    formatUpdoxPhone,
    formatAmountUSD,
    formatDateToAmerican,
  } from "@/utils/formatting";

  export default {
    name: "PaymentReceipt",
    components: {
      UpdoxFooter,
      UpdoxButton,
      UpdoxLoading,
      UpdoxError,
      UpdoxPaymentInvalidState,
      UpdoxBadPaymentRequestToken,
      UpdoxCannotProcessPayments,
    },
    data: () => ({
      loading: true,
      error: false,
      errorDetails: null,
      currentlySubmitting: false,
      receiptInfoStatus: "",
      receiptInvalidState: "",
      authorizationCode: "",
      practiceName: "",
      practicePhone: "",
      datePaid: "",
      amount: "",
    }),
    async mounted() {
      const { paymentRequestToken } = this.$route.params;

      await SimplePaymentsPatientService.getReceiptInfo(paymentRequestToken)
        .then((response) => {
          this.receiptInfoStatus = response.status;
          this.receiptInvalidState = response.invalidState;

          if (response.status === "OK") {
            this.practiceName = response.practiceName;
            this.authorizationCode = response.authorizationCode;
            this.practicePhone = formatUpdoxPhone(response.practicePhone);
            this.datePaid = formatDateToAmerican(response.datePaid);
            this.amount = formatAmountUSD(response.amount, {
              prefixSign: true,
            });
          } else if (response.status === "FAILED") {
            this.error = true;
            this.errorDetails = "";
          }
          this.loading = false;
        })
        .catch((error) => {
          let {
            response: { status: errorCode },
          } = error;
          if (typeof errorCode == "undefined" || 401 !== errorCode) {
            this.error = true;
            this.loading = false;
            this.errorDetails = error;
            /*airbrake.notify({
                error: e,
                message: "Unable to process payment token"
              });*/
          }
        });
    },
    methods: {
      onDownload() {
        const { paymentRequestToken } = this.$route.params;
        if (/^[-_A-Za-z0-9]{22}$/.test(paymentRequestToken)) {
          this.downloadPdf(
            `/api/pay/receipt/${paymentRequestToken}`,
            `UpdoxPaymentsReceipt_${paymentRequestToken}.pdf`
          );
        }
      },
      downloadPdf(url, name) {
        // Create a link element
        const link = document.createElement("a");

        link.href = url;
        link.download = name;
        link.target = "_blank";

        document.body.appendChild(link);

        // Dispatch click event on the link
        // This is necessary as link.click() does not work on the latest firefox
        link.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );

        // Remove link from body
        document.body.removeChild(link);
      },
    },
  };
</script>

<style scoped lang="scss">
/*We need to import the global styles for each view in order for the compiled web component to inherit the global and updox-styles styling*/
@import "@/assets/_scss/main";

  .loading-wrapper {
    padding-top: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .main-header {
    position: relative;
    color: #222;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    z-index: 101;
    width: 100%;

    .primary-header {
      position: relative;
      text-align: center;

      h2 {
        font-weight: 600;
      }

      small {
        font-size: 12px;
        margin-bottom: 10px;
      }
    }
  }

  .payment-preview {
    background: rgb(237, 246, 249);
    border: 1px solid rgba(0, 127, 173, 0.2);
    border-radius: 4px;

    .requested-details {
      padding: 10px;

      .flex-column {
        &:last-child {
          text-align: right;
        }
      }

      h2 {
        font-weight: 600;
        margin: 0;
      }

      small {
        color: rgb(68, 68, 68);
        font-size: 10px;
        font-weight: 400;
        font-style: italic;
      }
    }
  }

  .form-container {
    background-color: #fff;
    padding: 15px;

    h3 {
      margin-bottom: 5px;
    }

    p {
      margin-top: 0;
    }

    .form-field {
      display: flex;
      flex-direction: column;

      &:first-child {
        margin-top: 0;
      }
    }

    .form-body {
      margin-bottom: 20px;
    }

    .form-actions {
      p {
        font-size: 12px;
        margin: 0;
      }
    }
  }
</style>
